import React from "react";
import { Button, Input, Select, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

// Shared procedure-code / coverage-category editors + helpers, so the per-office
// override UI lives in exactly one place. Used by the Carrier Knowledge drawer
// (components/CRM/CarrierDrawerTabs) and the onboarding "procedure code defaults"
// step (Login/component/ProcedureCodeDefaults). The My Office "Insurance Carriers"
// page that first used them has been retired into Carrier Knowledge.

const { Text } = Typography;

export const DEFAULT_COVERAGE_CATEGORIES = ["Preventative", "Basic", "Major", "Orthodontics"];

export function formatCodesMultiline(codes) {
  if (!Array.isArray(codes) || codes.length === 0) return "";
  return codes
    .map((c) => String(c).trim())
    .filter(Boolean)
    .join("\n");
}

export function parseProcedureCodeTextarea(text) {
  if (!text || typeof text !== "string") return [];
  const lines = text.split(/\r?\n/);
  const out = [];
  for (const line of lines) {
    for (const part of line.split(",")) {
      const s = part.trim();
      if (s) out.push(s);
      if (out.length >= 50) return out;
    }
  }
  return out;
}

export function mapToRows(map) {
  if (!map || typeof map !== "object") return [];
  return Object.keys(map).map((code) => ({ code, category: map[code] }));
}

export function rowsToMap(rows) {
  const out = {};
  for (const r of rows || []) {
    const c = String((r && r.code) || "").trim();
    if (c && r.category) out[c] = r.category;
  }
  return out;
}

/**
 * Editable CDT-code -> coverage-category map, fully CONTROLLED over a `rows`
 * array of {code, category} held by the parent (so async-loaded data seeds
 * cleanly). Emits the updated array via onChange.
 *
 * `codeOptions` turns the code field from a free-text box into a searchable
 * dropdown of the codes that carrier actually asks about. Optional, because a
 * caller that cannot supply the list must not leave the user unable to type one;
 * without it the field behaves exactly as it always has.
 *
 * `getPopupContainer` must be passed when this renders inside a Drawer — antd
 * portals popups to document.body, which lands them behind the Drawer and the
 * dropdowns simply never appear to open.
 */
export function CategoryMapEditor({
  rows, onChange, validCategories, disabled, codeOptions, getPopupContainer,
}) {
  const list = Array.isArray(rows) ? rows : [];
  const cats =
    Array.isArray(validCategories) && validCategories.length
      ? validCategories
      : DEFAULT_COVERAGE_CATEGORIES;
  const useCodeDropdown = Array.isArray(codeOptions);
  const updateRow = (i, field, val) => {
    const next = list.slice();
    next[i] = { ...next[i], [field]: val };
    if (onChange) onChange(next);
  };
  const addRow = () => onChange && onChange([...list, { code: "", category: cats[0] }]);
  const removeRow = (i) => {
    const next = list.slice();
    next.splice(i, 1);
    if (onChange) onChange(next);
  };
  return (
    <div>
      {list.length === 0 && (
        <Text type="secondary" style={{ display: "block", fontSize: 13, marginBottom: 8 }}>
          No code overrides. Any code not listed uses the automatic category.
        </Text>
      )}
      {list.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          {useCodeDropdown ? (
            <Select
              style={{ flex: 1 }}
              showSearch
              placeholder="Pick a code"
              optionFilterProp="label"
              value={r.code || undefined}
              disabled={disabled}
              // A row loaded from saved data may name a code no longer in the
              // list. Including it keeps the row readable instead of blanking a
              // value the user never asked to lose.
              options={(r.code && codeOptions.indexOf(r.code) === -1
                ? [r.code].concat(codeOptions)
                : codeOptions
              ).map((c) => ({ value: c, label: c }))}
              onChange={(v) => updateRow(i, "code", v)}
              getPopupContainer={getPopupContainer}
              notFoundContent="No codes yet — add them above first."
            />
          ) : (
            <Input
              style={{ flex: 1 }}
              placeholder="CDT code (e.g. 33124)"
              value={r.code}
              disabled={disabled}
              onChange={(e) => updateRow(i, "code", e.target.value)}
            />
          )}
          <Select
            style={{ width: 170 }}
            value={r.category}
            disabled={disabled}
            options={cats.map((c) => ({ value: c, label: c }))}
            onChange={(v) => updateRow(i, "category", v)}
            getPopupContainer={getPopupContainer}
          />
          {!disabled && (
            <Button danger icon={<DeleteOutlined />} onClick={() => removeRow(i)} />
          )}
        </div>
      ))}
      {!disabled && (
        <Button type="dashed" icon={<PlusOutlined />} onClick={addRow} style={{ width: "100%" }}>
          Add code → category
        </Button>
      )}
    </div>
  );
}
